import type { NamedExoticComponent, ReactElement } from "react"
import { Fragment, memo } from "react"

import eq from "lodash-es/eq"
import map from "lodash-es/map"

import {
    ESpecialChar,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    SpecialChars,
    Typography
} from "@/3514/components"
import { type TUserState, useUser } from "@/3514/hooks"
import { buildCssClassName, isUserCoach } from "@/3514/utils"
import {
    type IUseTranslation,
    type TUseSubUserList,
    type TUseSubUserListAction,
    useSubUserList,
    useSubUserListAction,
    useTranslation
} from "@/hooks"
import { CoachWelcomeUserListItemImage } from "@/pages/coach-welcome/ui"
import type { TStoredSubUser } from "@/services"
import Avatar from "@/shared/avatar/Avatar"
import { Spinner } from "@/shared/spinner"

import "./HeaderDropdownProfileList.styles.scss"

type TProps = { listClassName?: string; titleClassName?: string }

const MemoizedProfileList: NamedExoticComponent<TProps> = memo(
    ({ listClassName = String(""), titleClassName = String("") }: TProps): ReactElement => {
        const { subUserList, isSubUserListLoading }: TUseSubUserList = useSubUserList.call({ msDebounce: 1000 })

        const { t }: IUseTranslation = useTranslation()

        const {
            user: { userId: currentUserId, id: currentId, role }
        }: TUserState = useUser()

        const isCoach: boolean = isUserCoach({ role } as never)

        const { handleAction }: TUseSubUserListAction = useSubUserListAction.call({
            shouldNavigateToNewTab: isCoach
        })

        return isSubUserListLoading ? (
            <Spinner width="20px" height="20px" className="mt-1" />
        ) : (
            <div className="dropdown-profile-list">
                <Typography
                    className={buildCssClassName("mb-0", { [titleClassName]: titleClassName })}
                    color={ETypographyColor.Black}
                    size={ETypographySize.Small}
                    weight={ETypographyWeight.Bold}
                    text={
                        <Fragment>
                            {t("shared.headerDropdown.switchProfile.label")}
                            {SpecialChars[ESpecialChar.Colon]}
                        </Fragment>
                    }
                />

                <ul
                    className={buildCssClassName({
                        "scrollable custom-scrollbar": subUserList.length > 4,
                        [listClassName]: listClassName
                    })}
                    role="menu"
                    onKeyDown={handleAction}
                    onClick={handleAction}
                >
                    {map<TStoredSubUser, ReactElement>(
                        subUserList,
                        ({ role, avatarColor, photo, id }: TStoredSubUser): ReactElement => {
                            const _isCurrent: boolean = eq(id, currentUserId ?? currentId)

                            return (
                                <li
                                    key={`sub-profile-${id}`}
                                    data-index={String(id)}
                                    tabIndex={_isCurrent ? -1 : 0}
                                    role="menuitem"
                                    className={buildCssClassName({ current: _isCurrent })}
                                >
                                    {!avatarColor ? (
                                        <Avatar url={photo} height={20} width={20} alt="coach-avatar" />
                                    ) : (
                                        <div>
                                            <CoachWelcomeUserListItemImage
                                                fillerColor={avatarColor}
                                                width={19}
                                                height={19}
                                            />
                                        </div>
                                    )}
                                    <Typography
                                        text={role}
                                        tag={ETypographyTag.Span}
                                        color={ETypographyColor.Black}
                                        size={ETypographySize.Small}
                                    />
                                </li>
                            )
                        }
                    )}
                </ul>
            </div>
        )
    }
)

MemoizedProfileList.displayName = "HeaderDropdownProfileList"

export { MemoizedProfileList as HeaderDropdownProfileList }
